import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  block,
  handleRequestError,
  scan,
  Scan,
  ScannerModule,
  ScannerStore,
  SOFTLINE_FEATURE_SCANNER,
} from '@softline/application';
import { Store } from '@softline/core';
import { FieldOkModalService } from '@softline/dynamic';
import { chooseFn, ModalService } from '@softline/ui-core';
import { LagerplatzAuswahlDialog } from '../../dialogs/lagerplatz-auswahl/lagerplatz-auswahl.dialog';
import {
  SOFTLINE_CONFIG_LAGERPLATZ_AUSWAHL,
  SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
} from '../../lager-core.shared';
import { StockyardRepository } from '../../repositories/stockyard.repository';
import { LagerplatzSucheStore } from '../../store/lagerplatz-suche.store';
import { Lagerplatz } from '../../types/lagerplatz';
import { LagerplatzInhalt } from '../../types/lagerplatz-inhalt';

import { TranslatePipe } from '@softline/ui-core';
import { UserWarehouseRepository } from '../../repositories/user-warehouse.repository';
@Component({
  selector: 'soft-lagerplatz-suche',
  imports: [CommonModule, ScannerModule, TranslatePipe],
  templateUrl: './lagerplatz-suche.component.html',
  styleUrls: ['./lagerplatz-suche.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [StockyardRepository, UserWarehouseRepository],
})
export class LagerplatzSucheComponent {
  private store = inject(Store);
  private injector = inject(Injector);

  private anwenderLagerRepository = inject(UserWarehouseRepository);
  private repository = inject(StockyardRepository);
  private fieldOkService = inject(FieldOkModalService);
  private modalService = inject(ModalService);
  config = inject(SOFTLINE_CONFIG_LAGERPLATZ_AUSWAHL);

  private chooseFn = chooseFn();

  loading$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
    LagerplatzSucheStore.getters.loading
  );
  loading = false;

  scan = output<Scan>();
  lagerplatzChange = output<Lagerplatz>();
  lagerplatzInhaltChange = output<LagerplatzInhalt>();

  readonly changeable = input(true);
  readonly forceLagerplatzInhalt = input(false);
  readonly lagerplatzInput = input<Lagerplatz | null | undefined>(undefined, {
    alias: 'lagerplatz',
  });
  readonly hideScan = input(false);
  readonly disableScan = input(false);
  readonly storageId = input<number | undefined>();

  readonly lagerplatz = linkedSignal(() => this.lagerplatzInput());
  readonly idbewekomm = input<number | null>(null);

  readonly canScan = computed(() => {
    return (
      this.changeable() && (this.config === 'scan' || this.config === 'scanAndSelect')
    );
  });

  readonly canSelect = computed(() => {
    return (
      this.changeable() && (this.config === 'select' || this.config === 'scanAndSelect')
    );
  });

  constructor() {
    scan(async (scan) => {
      if (this.hideScan() || this.disableScan()) return;
      if (!scan || this.loading) return;
      this.scan.emit(scan);
      this.loading = true;

      try {
        const lagerplatz = await this.store.dispatch(
          SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
          LagerplatzSucheStore.actions.suche,
          scan.data
        );

        this.loading = false;

        if (lagerplatz) {
          this.lagerplatz.set(lagerplatz);
          this.lagerplatzChange.emit(lagerplatz);
        }
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
      }
    });
  }

  async onScan(): Promise<void> {
    if (!this.canScan()) return;
    await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan, {});
  }

  async onOpenSelect(): Promise<void> {
    if (!this.canSelect()) return;
    const idBeweKomm = this.idbewekomm();
    let result: Lagerplatz | 'DISMISSED' | 'CANCELED' | null = null;
    if (idBeweKomm) {
      const stockyards = await block(async () => {
        try {
          return await this.repository.getAvailableStockyards(idBeweKomm);
        } catch (e) {
          handleRequestError(e, this.injector, { show: true });
        }
        return null;
      }, this.injector);
      if (!stockyards) return;
      const dialogResult = (await this.modalService.open({
        component: LagerplatzAuswahlDialog,
        title: 'Lagerplatz auswählen',
        data: {
          lagerplatzInhalte: stockyards,
        },
      })) as 'DISMISSED' | Lagerplatz;

      if (dialogResult === 'DISMISSED') return;

      const lagerplatzInhalt = stockyards.find(
        (o) => o.lagerplatz.id === dialogResult.id
      );

      if (lagerplatzInhalt) {
        this.lagerplatzInhaltChange.emit(lagerplatzInhalt);
      }
    } else {
      let lagerstammId = this.storageId();

      if (!lagerstammId) {

        try {
          const userWarehouses = await this.anwenderLagerRepository.getUserWarehouse();

          if (userWarehouses.length === 1) {
            lagerstammId = userWarehouses[0].id;
          } else if (userWarehouses.length > 0) {
            const selectedWarehouse = await this.chooseFn({
              title: 'Wälen Sie ein Lager aus',
              dismiss: true,
              options: userWarehouses.map((o) => {
                return {
                  value: o,
                  label: o.name1,
                };
              }),
            });

            if (selectedWarehouse === 'DISMISSED' || !selectedWarehouse?.id) return;

            lagerstammId = selectedWarehouse.id;
          }
        } catch (e) {
          console.error(e);
        }
      }

      result = await this.fieldOkService.open<Lagerplatz>(
        'lagerplatz',
        lagerstammId
          ? {
              idlagerstamm: lagerstammId,
            }
          : {},
        10_000
      );
    }

    if (!result || result === 'DISMISSED' || result === 'CANCELED') return;

    this.lagerplatz.set(result);
    this.lagerplatzChange.emit(result);
  }
}
